import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Custom hook for managing CRM lead notes
 */
export function useCrmNotes(leadId, refetch) {
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/lead-notes`, { lead_id: leadId, content: newNote });
      toast.success('Note added');
      setNewNote('');
      refetch();
    } catch { 
      toast.error('Failed to add note'); 
    } finally { 
      setAddingNote(false); 
    }
  };
  
  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/lead-notes/${noteId}`);
      toast.success('Note deleted');
      refetch();
    } catch { 
      toast.error('Failed to delete note'); 
    }
  };
  
  return {
    newNote,
    setNewNote,
    addingNote,
    handleAddNote,
    handleDeleteNote,
  };
}

/**
 * Custom hook for managing CRM lead tasks
 */
export function useCrmTasks(leadId, refetch) {
  const [taskForm, setTaskForm] = useState({ description: '', due_date: '' });
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const handleCreateTask = async () => {
    if (!taskForm.description.trim()) {
      toast.error('Description is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/tasks`, {
        lead_id: leadId,
        description: taskForm.description,
        due_date: taskForm.due_date || null,
      });
      toast.success('Task created');
      setTaskDialogOpen(false);
      setTaskForm({ description: '', due_date: '' });
      refetch();
    } catch { 
      toast.error('Failed to create task'); 
    } finally { 
      setSaving(false); 
    }
  };
  
  const handleToggleTask = async (taskId, currentStatus) => {
    try {
      const newStatus = currentStatus ? 'pending' : 'completed';
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus, completed: !currentStatus });
      toast.success(currentStatus ? 'Task reopened' : 'Task completed');
      refetch();
    } catch { 
      toast.error('Failed to update task'); 
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Task deleted');
      refetch();
    } catch { 
      toast.error('Failed to delete task'); 
    }
  };
  
  return {
    taskForm,
    setTaskForm,
    taskDialogOpen,
    setTaskDialogOpen,
    saving,
    handleCreateTask,
    handleToggleTask,
    handleDeleteTask,
  };
}

export default { useCrmNotes, useCrmTasks };
